import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post, setUnauthorizedHandler } from './api';
import { syncServerClock } from './clock';
import { qk } from './query';

export type Me = { id: string; name: string; email: string; serverNow: string; frozen?: boolean };

type AuthCtx = {
  user: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (me.data) syncServerClock(me.data.serverNow, { frozen: me.data.frozen });
  }, [me.data]);

  useEffect(() => {
    setUnauthorizedHandler(() => qc.setQueryData(qk.me, null));
  }, [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const u = await post<Me>('/auth/login', { email, password });
      syncServerClock(u.serverNow, { frozen: u.frozen });
      // limpa os dados de outra sessão sem derrubar o observador de ['me']
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(qk.me, u);
    },
    [qc],
  );

  const logout = useCallback(async () => {
    await post('/auth/logout').catch(() => undefined);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    qc.setQueryData(qk.me, null);
  }, [qc]);

  return (
    <Ctx.Provider value={{ user: me.data ?? null, loading: me.isLoading, login, logout }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth fora do AuthProvider');
  return c;
}
